/**
 * /medication-safety — Centennial Medication Safety Card
 *
 * Senior-friendly organiser for prescription medications. All user data is
 * stored locally in the browser (encrypted via secureStorage) and never
 * leaves the device. Free tier: 1 profile · 4 meds. Paid unlock: unlimited
 * meds and (family plan) 2 profiles.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Printer,
  Download,
  Trash2,
  AlertTriangle,
  ShieldCheck,
  Type as TypeIcon,
  UserPlus,
  Lock,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";
import useSEO from "@/lib/useSEO";
import {
  createCheckout,
  fetchMedLookalikes,
  upsertMedShareCard,
  deleteMedShareCard,
} from "@/lib/api";
import {
  loadProfiles,
  createProfile,
  updateProfile,
  deleteProfile,
  addMedication,
  updateMedication,
  deleteMedication,
  FREE_MED_LIMIT,
  FREE_PROFILE_LIMIT,
  UNLIMITED_PROFILES,
  getUnlock,
  getLargeText,
  setLargeText,
  exportAll,
  deleteAll,
} from "@/lib/medStorage";
import { MR_B_DEMO, DEMO_PROFILE_ID } from "@/lib/medDemo";
import { track, EVENTS } from "@/lib/analytics";
import MedicationForm from "@/components/med/MedicationForm";
import MedicationRow from "@/components/med/MedicationRow";
import SafetyCheckPanel from "@/components/med/SafetyCheckPanel";
import RefillCompareDialog from "@/components/med/RefillCompareDialog";
import DailyCheckoffChart from "@/components/med/DailyCheckoffChart";
import WalletCard from "@/components/med/WalletCard";
import CaregiverCard from "@/components/med/CaregiverCard";
import FeedbackForm from "@/components/med/FeedbackForm";
import MasterWeeklyCard from "@/components/med/MasterWeeklyCard";
import AppointmentCard from "@/components/med/AppointmentCard";
import QrShareCard from "@/components/med/QrShareCard";
import { exportMedCardPDF } from "@/lib/medPdfExport";

const DISCLAIMER_LONG =
  "This tool is for medication organization and education only. It does not replace a pharmacist, physician, prescription label, or professional medical advice. Do not start, stop, split, combine, or change a medication based on this tool. For an unknown or uncertain pill, keep it separate and contact a pharmacist. For a suspected poisoning or emergency, call emergency services or Poison Control (US: 1-800-222-1222).";

export default function MedicationSafety() {
  useSEO({
    title: "Centennial Medication Safety Card™ — Free Large-Print Weekly Med Card | Centennial",
    description:
      "Do you know what's in your medicine cabinet? Build a free, large-print Centennial Medication Safety Card — weekly master card, 5-way safety check, look-alike warnings, wallet list, caregiver and appointment copies. Private: everything stays in your browser.",
    canonicalPath: "/medication-safety",
  });

  const navigate = useNavigate();
  const [profiles, setProfiles] = useState(() => loadProfiles());
  const [activeId, setActiveId] = useState(() => {
    const list = loadProfiles();
    return list[0]?.id || null;
  });
  const [showDemo, setShowDemo] = useState(() => loadProfiles().length === 0);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showRefill, setShowRefill] = useState(false);
  const [largeText, setLargeTextState] = useState(() => getLargeText());
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [qrBusy, setQrBusy] = useState(false);
  const unlockToken = getUnlock();

  const activeProfile = useMemo(() => {
    if (showDemo) return MR_B_DEMO;
    return profiles.find((p) => p.id === activeId) || null;
  }, [profiles, activeId, showDemo]);

  useEffect(() => {
    if (largeText) document.documentElement.classList.add("med-large-text");
    else document.documentElement.classList.remove("med-large-text");
    return () => document.documentElement.classList.remove("med-large-text");
  }, [largeText]);

  const refresh = () => setProfiles(loadProfiles());

  const share = activeProfile && !activeProfile.is_demo ? activeProfile.share : null;
  const shareUrl = share?.card_id ? `${window.location.origin}/med-card/${share.card_id}` : "";

  useEffect(() => {
    if (!shareUrl) {
      setQrDataUrl("");
      return;
    }
    QRCode.toDataURL(shareUrl, {
      width: 480,
      margin: 1,
      color: { dark: "#1C2B23", light: "#FFFFFF" },
    })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(""));
  }, [shareUrl]);

  const sanitizeMeds = (meds) =>
    meds.map((m) => ({
      id: m.id || "",
      generic_name: m.generic_name || "",
      brand_name: m.brand_name || "",
      strength: m.strength || "",
      form: m.form || "",
      purpose: m.purpose || "",
      directions: m.directions || "",
      imprint_front: m.imprint_front || "",
      imprint_back: m.imprint_back || "",
      do_not_confuse: m.do_not_confuse || "",
      manufacturer: m.manufacturer || "",
      pharmacy: m.pharmacy || "",
      times: m.times || [],
    }));

  const syncShare = async (profileId) => {
    const p = loadProfiles().find((x) => x.id === profileId);
    if (!p?.share?.card_id) return;
    try {
      await upsertMedShareCard({
        name: p.name,
        meds: sanitizeMeds(p.meds),
        card_id: p.share.card_id,
        edit_key: p.share.edit_key,
      });
      updateProfile(profileId, {
        share: { ...p.share, synced_at: new Date().toISOString() },
      });
    } catch {
      /* silent background sync */
    }
  };

  const handleQrCreate = async () => {
    if (!activeProfile || activeProfile.is_demo) return;
    setQrBusy(true);
    try {
      const r = await upsertMedShareCard({
        name: activeProfile.name,
        meds: sanitizeMeds(activeProfile.meds),
        card_id: activeProfile.share?.card_id || "",
        edit_key: activeProfile.share?.edit_key || "",
      });
      updateProfile(activeProfile.id, {
        share: { card_id: r.id, edit_key: r.edit_key, synced_at: new Date().toISOString() },
      });
      refresh();
      toast.success("QR code created — it now prints on your cards.");
    } catch (e) {
      console.error(e);
      toast.error("Could not create the QR code. Try again.");
    } finally {
      setQrBusy(false);
    }
  };

  const handleQrRevoke = async () => {
    if (!activeProfile?.share?.card_id) return;
    if (!window.confirm("Turn off the QR code and delete this card's copy from the server?"))
      return;
    setQrBusy(true);
    try {
      await deleteMedShareCard(activeProfile.share.card_id, activeProfile.share.edit_key);
    } catch {
      /* already deleted */
    }
    updateProfile(activeProfile.id, { share: null });
    refresh();
    setQrBusy(false);
    toast.success("QR code turned off and server copy deleted.");
  };

  const handleCreateProfile = () => {
    const maxProfiles = unlockToken
      ? UNLIMITED_PROFILES.med_safety_unlock_family
      : FREE_PROFILE_LIMIT;
    if (profiles.length >= maxProfiles) {
      toast.message(
        unlockToken
          ? "Family plan supports up to 2 profiles."
          : "Free plan supports 1 profile — unlock for more.",
      );
      return;
    }
    const name = window.prompt("Name or initials for this profile:", "");
    if (!name) return;
    const p = createProfile(name);
    setProfiles(loadProfiles());
    setActiveId(p.id);
    setShowDemo(false);
    toast.success(`Profile "${p.name}" created — only on this device.`);
  };

  const handleAddMed = async (data) => {
    if (!activeProfile || activeProfile.is_demo) return;
    if (!unlockToken && activeProfile.meds.length >= FREE_MED_LIMIT) {
      toast.error("Free plan holds up to 4 medications. Unlock unlimited below.");
      return;
    }
    // Auto-suggest lookalikes if the user did not fill it in.
    if (!data.do_not_confuse && data.generic_name) {
      try {
        const r = await fetchMedLookalikes(data.generic_name);
        if (r.hints?.length) data.do_not_confuse = r.hints.join(", ");
      } catch {
        /* library lookup is optional */
      }
    }
    addMedication(activeProfile.id, data);
    refresh();
    setShowAdd(false);
    setEditing(null);
    toast.success("Medication added.");
    syncShare(activeProfile.id);
  };

  const handleUpdateMed = (medId, data) => {
    if (!activeProfile || activeProfile.is_demo) return;
    updateMedication(activeProfile.id, medId, data);
    refresh();
    setEditing(null);
    toast.success("Medication updated.");
    syncShare(activeProfile.id);
  };

  const handleDeleteMed = (medId) => {
    if (!activeProfile || activeProfile.is_demo) return;
    if (!window.confirm("Remove this medication from the card?")) return;
    deleteMedication(activeProfile.id, medId);
    refresh();
    syncShare(activeProfile.id);
  };

  const handleDeleteProfile = () => {
    if (!activeProfile || activeProfile.is_demo) return;
    if (!window.confirm(`Delete profile "${activeProfile.name}" and all its medications?`))
      return;
    if (activeProfile.share?.card_id) {
      deleteMedShareCard(activeProfile.share.card_id, activeProfile.share.edit_key).catch(() => {});
    }
    deleteProfile(activeProfile.id);
    const remaining = loadProfiles();
    setProfiles(remaining);
    setActiveId(remaining[0]?.id || null);
    if (remaining.length === 0) setShowDemo(true);
    toast.success("Profile deleted from this device.");
  };

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(exportAll(), null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `centennial-med-card-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded a JSON copy of your card.");
  };

  const handleWipe = () => {
    if (!window.confirm("Delete ALL medication profiles from this device? This cannot be undone."))
      return;
    deleteAll();
    setProfiles([]);
    setActiveId(null);
    setShowDemo(true);
    toast.success("All medication data cleared from this device.");
  };

  const handlePrint = () => {
    window.print();
  };

  const handlePDF = async () => {
    if (!activeProfile) return;
    try {
      await exportMedCardPDF(activeProfile);
      toast.success("Downloaded PDF of your medication card.");
    } catch (e) {
      console.error(e);
      toast.error("Could not export PDF. Try Print instead.");
    }
  };

  const buyUnlock = async (pkg) => {
    try {
      const { url } = await createCheckout(pkg);
      window.location.href = url;
    } catch (e) {
      console.error(e);
      toast.error("Could not open checkout. Try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] print:bg-white">
      {/* Header (hidden on print) */}
      <div className="max-w-6xl mx-auto px-6 py-6 print:hidden">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339]"
          >
            <ArrowLeft size={14} /> Back to calculator
          </Link>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="toggle-large-text"
              type="button"
              onClick={() => {
                const next = !largeText;
                setLargeTextState(next);
                setLargeText(next);
              }}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                largeText
                  ? "bg-[#2B5339] text-white border-[#2B5339]"
                  : "bg-white text-[#5C6B62] border-[#E5E2DC] hover:border-[#2B5339]"
              }`}
            >
              <TypeIcon size={13} /> Large text {largeText ? "on" : "off"}
            </button>
            <button
              data-testid="print-card"
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white border border-[#E5E2DC] hover:border-[#2B5339] text-[#5C6B62]"
            >
              <Printer size={13} /> Print
            </button>
            <button
              data-testid="pdf-card"
              type="button"
              onClick={handlePDF}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white border border-[#E5E2DC] hover:border-[#2B5339] text-[#5C6B62]"
            >
              <Download size={13} /> PDF
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-16 print:px-0 print:pb-0">
        {/* Hero */}
        <div className="print:hidden">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2 inline-flex items-center gap-2">
            <ShieldCheck size={12} /> Centennial Medication Safety Card™ · Know It · Check It · Take It Safely
          </div>
          <h1 className="med-h1 text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C2B23] leading-[1.05]">
            Do you know what&apos;s in<br />your medicine cabinet?
          </h1>
          <p className="mt-4 med-body text-lg text-[#5C6B62] leading-relaxed max-w-2xl">
            Keeping an accurate medication list makes conversations with family, caregivers,
            pharmacists, and doctors easier. Build a simple, large-print Centennial Medication
            Safety Card for yourself — or someone you care about. Free, private, printable:
            everything stays on this device.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              data-testid="cta-build-my-card"
              type="button"
              onClick={handleCreateProfile}
              className="bg-[#2B5339] text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-[#1C2B23]"
            >
              Build my medication card
            </button>
            <button
              data-testid="cta-help-a-parent"
              type="button"
              onClick={handleCreateProfile}
              className="bg-white border-2 border-[#2B5339] text-[#2B5339] rounded-full px-6 py-3 text-sm font-semibold hover:bg-[#E8F0EB]"
            >
              Help a parent or loved one
            </button>
            <button
              data-testid="cta-view-sample"
              type="button"
              onClick={() => setShowDemo(true)}
              className="bg-white border-2 border-[#C26747] text-[#C26747] rounded-full px-6 py-3 text-sm font-semibold hover:bg-[#FDF2ED]"
            >
              View Mr. B&apos;s sample card
            </button>
          </div>
        </div>

        {/* Profile selector */}
        <div
          data-testid="profile-bar"
          className="mt-8 flex flex-wrap gap-2 items-center print:hidden"
        >
          <button
            data-testid="pill-demo"
            type="button"
            onClick={() => setShowDemo(true)}
            className={`text-sm px-4 py-2 rounded-full border transition-colors ${
              showDemo
                ? "bg-[#F5C7B4] border-[#C26747] text-[#1C2B23]"
                : "bg-white border-[#E5E2DC] text-[#5C6B62] hover:border-[#C26747]"
            }`}
          >
            Mr. B (sample)
          </button>
          {profiles.map((p) => (
            <button
              key={p.id}
              data-testid={`pill-${p.id}`}
              type="button"
              onClick={() => {
                setActiveId(p.id);
                setShowDemo(false);
              }}
              className={`text-sm px-4 py-2 rounded-full border transition-colors ${
                !showDemo && activeId === p.id
                  ? "bg-[#2B5339] border-[#2B5339] text-white"
                  : "bg-white border-[#E5E2DC] text-[#5C6B62] hover:border-[#2B5339]"
              }`}
            >
              {p.name}
            </button>
          ))}
          <button
            data-testid="new-profile"
            type="button"
            onClick={handleCreateProfile}
            className="text-sm px-4 py-2 rounded-full bg-white border border-dashed border-[#2B5339] text-[#2B5339] hover:bg-[#E8F0EB] inline-flex items-center gap-1.5"
          >
            <UserPlus size={13} /> New profile
          </button>
        </div>

        {/* Demo banner */}
        {showDemo && (
          <div
            data-testid="demo-banner"
            className="mt-4 bg-[#FDF2ED] border border-[#F5C7B4] text-[#1C2B23] rounded-xl p-4 text-sm print:hidden"
          >
            <span className="font-medium">SAMPLE PROFILE — NOT MEDICAL INSTRUCTIONS.</span> Mr. B is a fictional demo —
            these medications, doses, and manufacturers are invented for demonstration only.
            <button
              data-testid="make-it-mine"
              type="button"
              onClick={handleCreateProfile}
              className="ml-2 inline-flex bg-[#2B5339] text-white rounded-full px-4 py-1.5 text-xs font-semibold hover:bg-[#1C2B23]"
            >
              Make it mine — build my card
            </button>
          </div>
        )}

        {/* Card header */}
        {activeProfile && (
          <>
            <SafetyCheckPanel largeText={largeText} onRefillCompare={() => setShowRefill(true)} />

            <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
              <h2 className="med-h2 text-2xl font-medium text-[#1C2B23]">
                {activeProfile.name}
                {activeProfile.is_demo && (
                  <span className="ml-2 text-xs bg-[#F5C7B4] text-[#1C2B23] rounded-full px-2 py-0.5 align-middle">
                    sample
                  </span>
                )}
              </h2>
              <div className="text-sm text-[#5C6B62]">
                {activeProfile.meds.length} medication{activeProfile.meds.length === 1 ? "" : "s"}
                {!unlockToken && !activeProfile.is_demo &&
                  ` · free plan limit ${FREE_MED_LIMIT}`}
              </div>
            </div>

            {/* Medication list */}
            <div className="mt-4 space-y-3">
              {activeProfile.meds.map((m) => (
                <MedicationRow
                  key={m.id}
                  m={m}
                  largeText={largeText}
                  onEdit={activeProfile.is_demo ? null : () => setEditing(m)}
                  onDelete={activeProfile.is_demo ? null : () => handleDeleteMed(m.id)}
                />
              ))}
              {activeProfile.meds.length === 0 && (
                <div
                  data-testid="empty-meds"
                  className="bg-white border border-dashed border-[#E5E2DC] rounded-xl p-8 text-center text-sm text-[#5C6B62]"
                >
                  No medications on this card yet.
                </div>
              )}
            </div>

            {/* Add / edit form */}
            {!activeProfile.is_demo && (
              <div className="mt-4 print:hidden">
                {!showAdd && !editing && (
                  <button
                    data-testid="add-med"
                    type="button"
                    onClick={() => setShowAdd(true)}
                    disabled={
                      !unlockToken && activeProfile.meds.length >= FREE_MED_LIMIT
                    }
                    className="inline-flex items-center gap-2 bg-[#2B5339] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#1C2B23] disabled:opacity-50"
                  >
                    <Plus size={15} /> Add medication
                  </button>
                )}
                {(showAdd || editing) && (
                  <MedicationForm
                    initial={editing || {}}
                    onSave={editing ? (d) => handleUpdateMed(editing.id, d) : handleAddMed}
                    onCancel={() => {
                      setShowAdd(false);
                      setEditing(null);
                    }}
                  />
                )}
              </div>
            )}

            {/* QR share — opt-in, own cards only */}
            {!activeProfile.is_demo && activeProfile.meds.length > 0 && (
              <div className="mt-8">
                <QrShareCard
                  share={share}
                  shareUrl={shareUrl}
                  qrDataUrl={qrDataUrl}
                  busy={qrBusy}
                  onCreate={handleQrCreate}
                  onSync={handleQrCreate}
                  onRevoke={handleQrRevoke}
                />
              </div>
            )}

            {/* Print-only outputs — everything below is what the user prints */}
            {activeProfile.meds.length > 0 && (
              <div className="mt-10 space-y-8">
                <MasterWeeklyCard profile={activeProfile} qr={qrDataUrl} />
                <DailyCheckoffChart profile={activeProfile} largeText={largeText} />
                <WalletCard profile={activeProfile} qr={qrDataUrl} />
                <CaregiverCard profile={activeProfile} />
                <AppointmentCard profile={activeProfile} qr={qrDataUrl} />
              </div>
            )}

            {/* Family feature */}
            {!activeProfile.is_demo && activeProfile.meds.length > 0 && (
              <div
                data-testid="family-block"
                className="mt-10 bg-[#E8F0EB] border border-[#2B5339] rounded-2xl p-6 print:hidden"
              >
                <h3 className="med-h2 text-xl font-medium text-[#1C2B23]">
                  Could this help someone you love?
                </h3>
                <p className="text-sm text-[#5C6B62] mt-1">
                  Most medication mistakes are caught by a family member or caregiver — not the
                  person taking the pills. Build a card for a parent, spouse, or friend.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    data-testid="create-another-card"
                    type="button"
                    onClick={handleCreateProfile}
                    className="bg-[#2B5339] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#1C2B23]"
                  >
                    Create another card
                  </button>
                  <button
                    data-testid="share-blank-card"
                    type="button"
                    onClick={() => {
                      const url = `${window.location.origin}/medication-safety`;
                      navigator.clipboard
                        .writeText(url)
                        .then(() => toast.success("Link copied — share the blank card, never your personal data."))
                        .catch(() => toast.message(`Copy this link to share: ${url}`));
                    }}
                    className="bg-white border border-[#2B5339] text-[#2B5339] rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#F6FAF7]"
                  >
                    Share the blank medication card
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Upgrade tile */}
        {!unlockToken && !showDemo && activeProfile && (
          <div
            data-testid="upgrade-tile"
            className="mt-10 bg-white border border-[#2B5339] rounded-2xl p-6 print:hidden"
          >
            <div className="flex items-start gap-3">
              <Lock size={18} className="text-[#2B5339] mt-1" />
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-1">
                  Unlock unlimited medications
                </div>
                <h3 className="text-xl font-medium text-[#1C2B23]">
                  One-time payment · lifetime unlock
                </h3>
                <p className="text-sm text-[#5C6B62] mt-2">
                  The free plan holds 4 medications for 1 profile. Unlock unlimited medications and
                  add a second profile for a spouse or parent.
                </p>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <button
                    data-testid="buy-single-unlock"
                    type="button"
                    onClick={() => buyUnlock("med_safety_unlock_single")}
                    className="text-left bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl p-4 hover:border-[#2B5339] hover:shadow-sm transition-all"
                  >
                    <div className="font-mono-num text-3xl text-[#1C2B23]">$11</div>
                    <div className="text-xs text-[#5C6B62]">one-time</div>
                    <div className="text-sm text-[#1C2B23] mt-2 font-medium">Single profile</div>
                    <div className="text-xs text-[#5C6B62] mt-1">Unlimited medications</div>
                  </button>
                  <button
                    data-testid="buy-family-unlock"
                    type="button"
                    onClick={() => buyUnlock("med_safety_unlock_family")}
                    className="text-left bg-[#E8F0EB] border border-[#2B5339] rounded-xl p-4 hover:shadow-sm transition-all"
                  >
                    <div className="font-mono-num text-3xl text-[#1C2B23]">$26</div>
                    <div className="text-xs text-[#5C6B62]">one-time</div>
                    <div className="text-sm text-[#1C2B23] mt-2 font-medium">Family · 2 profiles</div>
                    <div className="text-xs text-[#5C6B62] mt-1">Unlimited medications each</div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data controls */}
        {activeProfile && !activeProfile.is_demo && (
          <div className="mt-10 flex flex-wrap gap-3 print:hidden">
            <button
              data-testid="export-json"
              type="button"
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 text-xs bg-white border border-[#E5E2DC] rounded-full px-3 py-1.5 hover:border-[#2B5339] text-[#5C6B62]"
            >
              <Download size={12} /> Export my data
            </button>
            <button
              data-testid="delete-profile"
              type="button"
              onClick={handleDeleteProfile}
              className="inline-flex items-center gap-1.5 text-xs bg-white border border-[#E5E2DC] rounded-full px-3 py-1.5 hover:border-[#C26747] hover:text-[#C26747] text-[#5C6B62]"
            >
              <Trash2 size={12} /> Delete this profile
            </button>
            <button
              data-testid="wipe-all"
              type="button"
              onClick={handleWipe}
              className="inline-flex items-center gap-1.5 text-xs bg-white border border-[#E5E2DC] rounded-full px-3 py-1.5 hover:border-[#C26747] hover:text-[#C26747] text-[#5C6B62]"
            >
              <Trash2 size={12} /> Delete all data on this device
            </button>
          </div>
        )}

        {/* Long disclaimers */}
        <div
          data-testid="disclaimers"
          className="mt-14 bg-white border border-[#E5E2DC] rounded-2xl p-6 print:mt-8"
        >
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#C26747] mb-3 inline-flex items-center gap-1.5">
            <AlertTriangle size={11} /> Important notices
          </div>
          <p className="text-sm text-[#1C2B23] leading-relaxed">{DISCLAIMER_LONG}</p>
          <p className="text-xs text-[#5C6B62] mt-3 leading-relaxed">
            Privacy: all data lives only in your browser (encrypted local storage). We do not
            request Social Security numbers or insurance details. We do not use medication
            information for advertising or share it with anyone. If you choose to create a QR
            code, a photo-free copy of that card (names, doses, directions) is stored on our
            server so the link works on any phone — you can delete it at any time with one click.
          </p>
        </div>

        {/* Feedback */}
        <div className="mt-6">
          <FeedbackForm context={activeProfile ? `profile:${activeProfile.name}` : ""} />
        </div>
      </div>

      {showRefill && <RefillCompareDialog onClose={() => setShowRefill(false)} />}

      {/* Print-only styles */}
      <style>{`
        @media print {
          @page { margin: 0.4in; }
          body { background: #fff !important; }
        }
        html.med-large-text .med-h1 { font-size: 3.5rem !important; line-height: 1.05; }
        html.med-large-text .med-h2 { font-size: 2.25rem !important; }
        html.med-large-text .med-body,
        html.med-large-text .med-row-name,
        html.med-large-text .med-row-strength { font-size: 1.35rem !important; line-height: 1.5; }
        html.med-large-text .med-row-meta { font-size: 1.05rem !important; }
      `}</style>
    </div>
  );
}
